import { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useGetStepDetailsQuery } from '../../../Services/PathwayAdminAPI';
import '../../../Styles/StepPreview.css';

const StepPreview = ({ show, onHide, stepId }) => {
  const { data: stepData, isLoading, error } = useGetStepDetailsQuery(stepId, {
    skip: !stepId || !show,
  });
  const [currentPage, setCurrentPage] = useState(0);

  const step = stepData?.data;

  // Reset to first page when modal opens or step changes
  useEffect(() => {
    if (show) {
      setCurrentPage(0);
    }
  }, [show, stepId]);

  // Calculate total pages based on available content
  const getTotalPages = () => {
    if (!step) return 0;
    let pages = 0;
    
    // Page 1: Title + Objective (always present if step exists)
    pages++;
    
    // Page 2: Key Actions (if available)
    if (step.key_actions && step.key_actions.length > 0) {
      pages++;
    }
    
    // Page 3: Quick Insight + Tags (if available)
    if (step.quick_insight || (step.tags && step.tags.length > 0)) {
      pages++;
    }
    
    // Page 4+: Exercises (one page per exercise)
    if (step.exercises && step.exercises.length > 0) {
      pages += step.exercises.length;
    }
    
    return pages;
  };

  const totalPages = getTotalPages();

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleClose = () => {
    setCurrentPage(0);
    onHide();
  };

  const renderFieldPreview = (field) => {
    switch (field.field_type) {
      case 'text':
        return (
          <div className="preview-field" key={field.id}>
            <label className="preview-field-label">
              {field.label}
              {field.is_required && <span className="required-indicator">*</span>}
            </label>
            <input
              type="text"
              className="preview-field-input"
              placeholder={field.placeholder || ''}
              disabled
            />
          </div>
        );

      case 'textarea':
        return (
          <div className="preview-field" key={field.id}>
            <label className="preview-field-label">
              {field.label}
              {field.is_required && <span className="required-indicator">*</span>}
            </label>
            <textarea
              className="preview-field-textarea"
              placeholder={field.placeholder || ''}
              rows={4}
              disabled
            />
          </div>
        );

      case 'radio':
        return (
          <div className="preview-field preview-field-radio" key={field.id}>
            <label className="preview-field-label">
              {field.label}
              {field.is_required && <span className="required-indicator">*</span>}
            </label>
            <div className="preview-radio-group">
              {field.options?.map((option, idx) => {
                const optionValue = typeof option === 'string' ? option : option.value;
                const optionLabel = typeof option === 'string' ? option : option.label;
                const optionDesc = typeof option === 'string' ? '' : option.description;
                
                return (
                  <label key={idx} className="preview-radio-item">
                    <div className="preview-radio-input-wrapper">
                      <input
                        type="radio"
                        name={`preview-field-${field.id}`}
                        value={optionValue}
                        disabled
                        className="preview-radio-input"
                      />
                      <span className="preview-radio-custom"></span>
                    </div>
                    <div className="preview-radio-text">
                      <div className="preview-radio-title">{optionLabel}</div>
                      {optionDesc && (
                        <div className="preview-radio-desc">{optionDesc}</div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        );

      case 'dropdown':
        return (
          <div className="preview-field" key={field.id}>
            <label className="preview-field-label">
              {field.label}
              {field.is_required && <span className="required-indicator">*</span>}
            </label>
            <select className="preview-field-select" disabled>
              <option>Select an option</option>
              {field.options?.map((option, idx) => {
                const optionLabel = typeof option === 'string' ? option : option.label;
                return <option key={idx}>{optionLabel}</option>;
              })}
            </select>
          </div>
        );

      default:
        return null;
    }
  };

  // Render step title header (shown on all pages)
  const renderStepHeader = () => {
    if (!step) return null;
    return (
      <div className="preview-step-header-section">
        <h2 className="preview-step-title">{step.title}</h2>
        {step.step_number && (
          <span className="preview-step-number">Step {step.step_number}</span>
        )}
      </div>
    );
  };

  // Render current page content
  const renderPageContent = () => {
    if (!step) return null;

    let pageIndex = 0;

    // Page 1: Objective
    if (currentPage === pageIndex) {
      return (
        <div className="preview-page">
          {step.objective && (
            <div className="preview-section">
              <h3 className="preview-section-heading">Objective</h3>
              <p className="preview-objective" dangerouslySetInnerHTML={{ __html: step.objective }} />
            </div>
          )}
        </div>
      );
    }
    pageIndex++;

    // Page 2: Key Actions
    if (step.key_actions && step.key_actions.length > 0) {
      if (currentPage === pageIndex) {
        return (
          <div className="preview-page">
            <div className="preview-section">
              <h3 className="preview-section-heading">Key Actions</h3>
              <ul className="preview-key-actions">
                {step.key_actions.map((action, idx) => (
                  <li key={idx} className="preview-key-action-item">
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      }
      pageIndex++;
    }

    // Page 3: Quick Insight + Tags
    if (step.quick_insight || (step.tags && step.tags.length > 0)) {
      if (currentPage === pageIndex) {
        return (
          <div className="preview-page">
            {step.quick_insight && (
              <div className="preview-section preview-insight-section">
                <div className="preview-insight-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2C12 2 9 5 9 9C9 11.21 10.79 13 13 13C15.21 13 17 11.21 17 9C17 5 14 2 12 2Z" stroke="#FD9C00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 13V17M14 13V17M10 17H14M10 17C10 18.1046 10.8954 19 12 19C13.1046 19 14 18.1046 14 17M8 21H16" stroke="#FD9C00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="preview-insight-content">
                  <h3 className="preview-section-heading">Quick Insight</h3>
                  <p className="preview-quick-insight">{step.quick_insight}</p>
                </div>
              </div>
            )}

            {step.tags && step.tags.length > 0 && (
              <div className="preview-section">
                <h3 className="preview-section-heading">Tags</h3>
                <div className="preview-tags">
                  {step.tags.map((tag, idx) => (
                    <span key={idx} className="preview-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }
      pageIndex++;
    }

    // Pages 4+: Exercises (one per page)
    if (step.exercises && step.exercises.length > 0) {
      const exerciseIndex = currentPage - pageIndex;
      if (exerciseIndex >= 0 && exerciseIndex < step.exercises.length) {
        const exercise = step.exercises[exerciseIndex];
        return (
          <div className="preview-page">
            <div className="preview-section preview-exercises-section">
              <h3 className="preview-section-heading">Exercise</h3>
              <div className="preview-exercise">
                <h4 className="preview-exercise-title">{exercise.title}</h4>
                {exercise.description && (
                  <p className="preview-exercise-description">{exercise.description}</p>
                )}
                {exercise.fields && exercise.fields.length > 0 && (
                  <div className="preview-exercise-fields">
                    {exercise.fields.map((field) => renderFieldPreview(field))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      }
    }

    return null;
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg" className="step-preview-modal">
      <Modal.Body className="step-preview-modal-body">
        <div className="step-preview-container">
          <div className="step-preview-header">
            <h5>Step Preview</h5>
            <div className="preview-header-right">
              {totalPages > 0 && (
                <span className="preview-page-indicator">
                  Page {currentPage + 1} of {totalPages}
                </span>
              )}
              <button className="btn-close-preview" onClick={handleClose}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>

          {isLoading ? (
            <div className="preview-loading">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="spinner-icon">
                <circle cx="12" cy="12" r="10" stroke="#FD9C00" strokeWidth="2" strokeLinecap="round" strokeDasharray="32" strokeDashoffset="32">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite"/>
                </circle>
              </svg>
              <p>Loading step preview...</p>
            </div>
          ) : error ? (
            <div className="preview-error">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="#dc3545" strokeWidth="2"/>
                <path d="M12 8V12M12 16H12.01" stroke="#dc3545" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <p>Error loading step preview: {error.message || 'Unknown error'}</p>
            </div>
          ) : step ? (
            <>
              <div className="step-preview-content">
                {renderStepHeader()}
                <div className="preview-content-scrollable">
                  {renderPageContent()}
                </div>
              </div>

              {totalPages > 1 && (
                <div className="preview-navigation">
                  <button
                    className="btn-nav btn-previous"
                    onClick={handlePrevious}
                    disabled={currentPage === 0}
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Previous
                  </button>
                  <div className="preview-dots">
                    {Array.from({ length: totalPages }).map((_, idx) => (
                      <button
                        key={idx}
                        className={`preview-dot ${currentPage === idx ? 'active' : ''}`}
                        onClick={() => setCurrentPage(idx)}
                        aria-label={`Go to page ${idx + 1}`}
                      />
                    ))}
                  </div>
                  <button
                    className="btn-nav btn-next"
                    onClick={handleNext}
                    disabled={currentPage === totalPages - 1}
                  >
                    Next
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="preview-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20M16 13H8M16 17H8M10 9H8" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p>No step data available</p>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default StepPreview;
